
import { Link, useNavigate } from "react-router-dom";
import { Plus, Tags, MoreVertical, Settings, Loader2 } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildProfileActions } from "@/components/actions";
import { Profile } from "@/types/profile";
import { useProfiles } from "@/hooks/useProfiles";
import { toast } from "sonner";
import { PlatformIcons } from "./PlatformIcons";
import { useQueryClient } from "@tanstack/react-query";
import * as policiesApi from "@/services/policiesApi";
import { Button } from "@/components/ui/button";
import CustomerTag from "@/components/CustomerTag";
import { useCustomerNames } from "@/hooks/useCustomerNames";
import { useSingleTenant } from "@/hooks/useSingleTenant";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useMemo } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { SortableHeader } from "@/components/ui/table-cells";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardFooter,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { getIconComponent } from "@/utils/profileIcons";

interface ProfilesListProps {
  profiles: Profile[];
  view: "grid" | "list";
  searchQuery?: string;
}

export const ProfilesList = ({ profiles, view, searchQuery = "" }: ProfilesListProps) => {
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [selectedProfileForTags, setSelectedProfileForTags] = useState<{ id: string; name: string } | null>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Profile | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const singleTenant = useSingleTenant();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { deleteProfile } = useProfiles();
  const queryClient = useQueryClient();


  // Collect all unique customer IDs for reactive resolution
  const allCustomerIds = useMemo(() => {
    const ids = new Set<string>();
    profiles.forEach(p => {
      (p.customers || []).forEach(id => ids.add(id));
      if (p.customer) ids.add(p.customer);
    });
    return Array.from(ids);
  }, [profiles]);

  const resolvedNames = useCustomerNames(allCustomerIds);
  const customerNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    allCustomerIds.forEach((id, i) => { map[id] = resolvedNames[i]; });
    return map;
  }, [allCustomerIds, resolvedNames]);

  const getCustomerName = (id: string) => customerNameMap[id] || id;

  const openTagAssignment = (profile: Profile) => {
    setSelectedProfileForTags({ id: profile.id, name: profile.name });
    setIsTagAssignmentOpen(true);
  };

  const handleDuplicate = async (profile: Profile) => {
    try {
      const original = await policiesApi.getPolicy(profile.id);
      const payloads = (original.payloads || []).map(p => ({
        kind: p.kind || '',
        identifier: p.identifier || '',
        config: p.configJson ? JSON.parse(p.configJson) : {},
      }));
      await policiesApi.createPolicy({
        name: `Duplicate - ${original.name}`,
        description: original.description,
        payloads,
      });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success("Profile duplicated successfully");
    } catch (error) {
      toast.error("Failed to duplicate profile");
    }
  };

  const getProfileActions = (profile: Profile) =>
    buildProfileActions({
      surface: "list",
      onView: () => navigate(`/profiles/${profile.id}`),
      onEdit: () => navigate(`/configurations/${profile.id}/edit`),
      onDuplicate: () => setDuplicateTarget(profile),
      onAssignTags: () => openTagAssignment(profile),
      onDelete: () => setDeleteTarget({ id: profile.id, name: profile.name }),
    });

  const columns: ColumnDef<Profile>[] = [
    {
      accessorKey: "icon",
      header: "",
      cell: ({ row }) => {
        const ProfileIcon = row.original.icon 
          ? getIconComponent(row.original.icon) 
          : Settings;
        return (
          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
            <ProfileIcon className="h-4 w-4 text-primary" />
          </div>
        );
      },
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="font-medium">
          <Link 
            to={`/profiles/${row.original.id}`}
            className="hover:text-primary underline-offset-4 hover:underline"
          >
            {row.getValue("name")}
          </Link>
          {row.original.isDraft && (
            <span className="ml-2 text-xs bg-warning-muted text-warning-muted-foreground px-2 py-0.5 rounded-full">
              Draft
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
      cell: ({ row }) => <div>{row.getValue("description")}</div>,
    },
    {
      accessorKey: "platforms",
      header: "Platforms",
      cell: ({ row }) => (
        <PlatformIcons platforms={row.getValue("platforms")} size="sm" />
      ),
    },
    ...(!singleTenant ? [{
      id: "customer" as const,
      header: "Customer",
      cell: ({ row }: { row: any }) => {
        const profile = row.original;
        if (profile.allCustomers) {
          return <span className="text-sm text-muted-foreground">All Customers</span>;
        }
        if (profile.customers && profile.customers.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {profile.customers.map((customerId: string) => (
                <CustomerTag key={customerId} customer={getCustomerName(customerId)} />
              ))}
            </div>
          );
        }
        if (profile.customer) {
          return <CustomerTag customer={getCustomerName(profile.customer)} />;
        }
        return <span className="text-sm text-muted-foreground">—</span>;
      },
    }] : []),
    {
      id: "tags",
      header: ({ column }: any) => <SortableHeader column={column}>Tags</SortableHeader>,
      cell: ({ row }: { row: any }) => (
        <TagsDisplay 
          resourceId={row.original.id} 
          resourceType="profile" 
          size="sm" 
          maxTags={3}
          showRemoveButton
        />
      ),
    },
    {
      accessorKey: "payloads",
      header: ({ column }) => <SortableHeader column={column}>Payloads</SortableHeader>,
      cell: ({ row }) => (
        <div>{row.original.payloads.length}</div>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <ActionsDropdown actions={getProfileActions(profile)} />
        );
      },
    },
  ];

  const safeProfiles = Array.isArray(profiles) ? profiles : [];

  // Filter profiles based on search query
  const filteredProfiles = safeProfiles.filter((profile) =>
    (profile.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (profile.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (Array.isArray(profile.platforms) ? profile.platforms : []).some(platform =>
      (platform || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  if (filteredProfiles.length === 0) {
    return (
      <div className="text-center py-12">
        {safeProfiles.length === 0 ? (
          <>
            <p className="text-muted-foreground text-lg">No profiles found</p>
            <div className="mt-4">
              <Button asChild>
                <Link to="/profiles/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create your first profile
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-lg">No profiles match your search.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your search criteria.
            </p>
          </>
        )}
      </div>
    );
  }

  const mobileList = (
    <div className="flex flex-col gap-2">
      {filteredProfiles.map((profile) => {
        const ProfileIcon = profile.icon ? getIconComponent(profile.icon) : Settings;
        return (
          <div
            key={profile.id}
            className="flex items-center gap-3 p-3 rounded-lg border bg-card active:bg-accent/40 transition-colors"
          >
            <Link
              to={`/profiles/${profile.id}`}
              className="flex items-center gap-3 flex-1 min-w-0"
            >
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ProfileIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{profile.name}</span>
                  {profile.isDraft && (
                    <span className="text-[10px] bg-warning-muted text-warning-muted-foreground px-1.5 py-0.5 rounded-full">
                      Draft
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                  <PlatformIcons platforms={profile.platforms} size="sm" />
                  <span>·</span>
                  <span>{profile.payloads.length} payload{profile.payloads.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </Link>
            <ActionsDropdown actions={getProfileActions(profile)} />
          </div>
        );
      })}
    </div>
  );

  const content = isMobile ? mobileList : view === "grid" ? (

    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      {filteredProfiles.map((profile) => {
        const ProfileIcon = profile.icon ? getIconComponent(profile.icon) : Settings;
        
        return (
          <GlassCard key={profile.id} variant="default">
            <GlassCardHeader className="pb-2 p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ProfileIcon className="h-5 w-5 text-primary" />
                  </div>
                  <Link 
                    to={`/profiles/${profile.id}`}
                    className="text-lg font-semibold hover:text-primary transition-colors block"
                  >
                    {profile.name}
                  </Link>
                </div>
                <ActionsDropdown actions={getProfileActions(profile)} />
              </div>
              <GlassCardDescription className="mt-1">{profile.description}</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="space-y-2 p-4 pt-0">
              <p className="text-sm text-muted-foreground">
                {profile.payloads.length} payload{profile.payloads.length !== 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                <TagsDisplay 
                  resourceId={profile.id} 
                  resourceType="profile" 
                  maxTags={3}
                  showRemoveButton
                />
              </div>
              {profile.isDraft && (
                <span className="text-xs bg-warning-muted text-warning-muted-foreground px-2.5 py-1 rounded-full">
                  Draft
                </span>
              )}
              <div className="mt-2 pt-2 border-t flex items-center justify-between">
                <PlatformIcons platforms={profile.platforms} />
                {!singleTenant && (
                  <span className="text-xs text-muted-foreground">
                    {profile.allCustomers
                      ? "All Customers"
                      : profile.customers && profile.customers.length > 0
                        ? profile.customers.map(id => getCustomerName(id)).join(", ")
                        : profile.customer
                          ? getCustomerName(profile.customer)
                          : null}
                  </span>
                )}
              </div>
            </GlassCardContent>
          </GlassCard>
        );
      })}
    </div>
  ) : (
    <DataTable
      data={filteredProfiles}
      columns={columns}
      enableRowSelection={true}
      enableColumnResizing={true}
      enableSorting={true}
      columnSizing={columnSizing}
      onColumnSizingChange={setColumnSizing}
      emptyMessage={
        safeProfiles.length === 0 
          ? "No profiles found. Create your first profile to get started."
          : "No profiles match your search. Try adjusting your search criteria."
      }
    />
  );

  return (
    <>
      {content}
      
      {selectedProfileForTags && (
        <TagAssignmentDialog
          open={isTagAssignmentOpen}
          onOpenChange={setIsTagAssignmentOpen}
          resourceId={selectedProfileForTags.id}
          resourceType="profile"
          resourceName={selectedProfileForTags.name}
          onAssignmentComplete={() => {
            setSelectedProfileForTags(null);
            // Optionally refresh profile data to show updated tags
          }}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">"{deleteTarget?.name}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={async (e) => {
                e.preventDefault();
                if (deleteTarget) {
                  setIsDeleting(true);
                  try {
                    await deleteProfile(deleteTarget.id);
                  } finally {
                    setIsDeleting(false);
                    setDeleteTarget(null);
                  }
                }
              }}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!duplicateTarget} onOpenChange={(open) => !open && setDuplicateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a copy of <span className="font-semibold">{duplicateTarget?.name}</span> with all its payloads. The new configuration will be named <span className="font-semibold">Duplicate - {duplicateTarget?.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDuplicating}
              onClick={async (e) => {
                e.preventDefault();
                if (duplicateTarget) {
                  setIsDuplicating(true);
                  await handleDuplicate(duplicateTarget);
                  setIsDuplicating(false);
                  setDuplicateTarget(null);
                }
              }}
            >
              {isDuplicating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isDuplicating ? "Duplicating…" : "Duplicate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
